import classNames from 'classnames'
import { subYears } from 'date-fns'
import isAfter from 'date-fns/isAfter'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { FormEventHandler, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import styles from '../PersonalDetailsPage.module.scss'

import 'react-datepicker/dist/react-datepicker.css'

import { Form } from '@lib/formik'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { Radio } from '@ui/components/Radio'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { getMonthOptions } from '@utils/date.utils'
import { FullNameLettersPattern } from '@utils/formValidation'
import { preventPaste } from '@utils/input.utils'
import { isChinese, isZero } from '@utils/validations'

import { LeadMembersEnum } from '@model/CreateLead'

import { createFormField } from '@global/formField/FormField'
import { createFormSelectField } from '@global/formField/FormSelectField'
import { createFormRadioField } from '@global/formField/RadioGroupField'

import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'

export interface PersonalDetailsStep1FormValues {
  genderId: string
  firstName: string
  lastName: string
  middleName: string
  nativeName?: string
  monthOfBirth: string
  dayOfBirth: string
  yearOfBirth: string
  isUsCitizen?: boolean
}

export type FormValues = PersonalDetailsStep1FormValues
const FormField = createFormField<PersonalDetailsStep1FormValues>()
const FormSelectField = createFormSelectField<PersonalDetailsStep1FormValues>()
const FormRadioGroupField = createFormRadioField<PersonalDetailsStep1FormValues>()

const PersonalDetailsStep1FormUI: React.FC<
  FormikProps<PersonalDetailsStep1FormValues> & OuterProps
> = (props) => {
  const { values, isValid, handleSubmit, setFieldValue } = props
  useOnLocaleSwitch(() => props.validateForm())

  const { t } = useTranslation()

  const genderRef = useRef<HTMLInputElement>(null)
  const ref = useRef<HTMLFormElement>(null)

  const [isUsCitizen, setIsUsCitizen] = useState(false)

  const locale = useSessionLanguage()

  const onSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()
    props.validateForm()
    if (!genderRef?.current?.value) {
      ref?.current?.scrollIntoView({ behavior: 'smooth' })
    }
    if (genderRef?.current?.checkValidity()) {
      ref?.current?.scrollIntoView({ behavior: 'smooth' })
      genderRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
    if (isUsCitizen) {
      setFieldValue('isUsCitizen', true)
    }

    handleSubmit()
  }

  document.addEventListener('wheel', () => {
    if ((document?.activeElement as any)?.type === 'number') {
      ;(document?.activeElement as any)?.blur()
    }
  })

  const onKeyDown = (e: { key: string; preventDefault: () => any }) =>
    ['e', 'E', '+', '-', '.', ','].includes(e.key) && e.preventDefault()

  const genders = [
    { id: '1', name: t('Mr') },
    { id: '2', name: t('Mrs/Ms') },
  ]

  return (
    <Form className={styles.form} onSubmit={onSubmit} ref={ref} autoComplete='off'>
      <Heading6 medium>{t('Sign up.Title and Name')}</Heading6>
      <Box mb='x12'>
        <FormRadioGroupField horizontal name='genderId' value={values.genderId}>
          {genders.map(({ id, name }) => (
            <Radio key={id} value={id}>
              {name}
            </Radio>
          ))}
        </FormRadioGroupField>
      </Box>
      <FieldWrapper>
        <FormField type='text' required name='firstName' label={t('Sign up.First name')} />

        <FormField type='text' name='middleName' label={t('Sign up.Middle name')} showLabel />

        <FormField type='text' required name='lastName' label={t('Sign up.Last name')} showLabel />

        {locale === 'zh' && (
          <div className={styles.nativeNameContainer}>
            <Body1>{t('Sign up.Please fill in your native name with Chinese characters')}</Body1>

            <FormField type='text' name='nativeName' label={t('Sign up.Native Name')} showLabel />
          </div>
        )}
      </FieldWrapper>
      <Heading6 medium>{t('Sign up.Date of Birth')}</Heading6>
      <div className={styles.dateContainer}>
        <div>
          <FormField
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            autoComplete='false'
            name='dayOfBirth'
            label={t('Day')}
            className={styles.dateForm}
            value={values.dayOfBirth.toString().replace(/^0+/, '')}
            required
            onKeyDown={onKeyDown}
            onPaste={preventPaste}
          />
        </div>
        <div className={classNames(styles.dateForm, styles.monthField)}>
          <FormSelectField
            name='monthOfBirth'
            label={t('Month')}
            showLabel
            required
            options={getMonthOptions(t).map((option) => ({
              id: option.id.toString(),
              name: option.label,
            }))}
            hasCheckMark={false}
            dropdownListVariant='compact'
            readOnly={true}
          />
        </div>
        <div>
          <FormField
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            autoComplete='false'
            name='yearOfBirth'
            label={t('Year')}
            value={values.yearOfBirth.toString().replace(/^0+/, '')}
            className={styles.dateForm}
            required
            onKeyDown={onKeyDown}
            onPaste={preventPaste}
          />
        </div>
      </div>
      <Heading6 medium>
        {t('Sign up.US Citizen')}{' '}
        <Tooltip content={t("Sign up.Unfortunately, we can't offer our services to US citizens")}>
          <ActionIcon icon={InfoIcon} size='x20' />
        </Tooltip>
      </Heading6>
      <Checkbox checked={isUsCitizen} onChange={() => setIsUsCitizen((prevState) => !prevState)}>
        {t('Sign up.I am a US Citizen for tax purposes')}
      </Checkbox>
      <div className={styles.button}>
        <Button.Primary
          size='lg'
          fullWidth
          iconRight={ArrowIcon.Right}
          disabled={props.isSubmitting || !isValid}
          onClick={onSubmit}
        >
          {t('Next')}
        </Button.Primary>
      </div>
    </Form>
  )
}

interface OuterProps {
  values: Partial<PersonalDetailsStep1FormValues>

  onSubmit(values: PersonalDetailsStep1FormValues): void
}

export const PersonalDetailsStep1Form = withFormik<OuterProps, PersonalDetailsStep1FormValues>({
  mapPropsToValues: ({ values }) => {
    const searchParams = new URLSearchParams(window.location.search)

    const birthdayParam = searchParams.get(LeadMembersEnum.BIRTHDAY)
    const birthday = birthdayParam ? new Date(birthdayParam) : null
    const isValidBirthday = birthday instanceof Date && !isNaN(birthday.getTime())

    const birthDate = isValidBirthday ? birthday.getDate().toString() : ''
    const birthMonth = isValidBirthday ? (birthday.getMonth() + 1).toString() : ''
    const birthYear = isValidBirthday ? birthday.getFullYear().toString() : ''

    return {
      firstName: values.firstName || searchParams.get(LeadMembersEnum.FIRST_NAME) || '',
      genderId: values.genderId || searchParams.get(LeadMembersEnum.GENDER_ID) || '',
      middleName: values.middleName || searchParams.get(LeadMembersEnum.MIDDLE_NAME) || '',
      dayOfBirth: values.dayOfBirth || birthDate,
      monthOfBirth: values.monthOfBirth || birthMonth,
      yearOfBirth: values.yearOfBirth || birthYear,
      lastName: values.lastName || searchParams.get(LeadMembersEnum.LAST_NAME) || '',
      nativeName: values.nativeName || searchParams.get(LeadMembersEnum.NATIVE_NAME) || '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<PersonalDetailsStep1FormValues> = {}
    function isLeapYear(year: number) {
      return (isZero(year % 4) && year % 100 !== 0) || isZero(year % 400)
    }

    const currentYear = new Date().getFullYear()
    const eighteenYearsAgo = currentYear - 18

    const dayOfBirth = Number(values.dayOfBirth)
    const monthOfBirth = Number(values.monthOfBirth)
    const yearOfBirth = Number(values.yearOfBirth)

    if (!FullNameLettersPattern.test(values.firstName)) {
      errors.firstName = t('Validation.First name contains unsupported characters')
    }

    if (!values.firstName || values.firstName.length < 2) {
      errors.firstName = t('Validation.At least characters', {
        length: 2,
      })
    }

    if (values.firstName && values.firstName.length > 64) {
      errors.firstName = t('Validation.First name must be 64 characters or fewer', {
        amount: 64,
      })
    }

    if (values.nativeName && !isChinese(values.nativeName)) {
      errors.nativeName = t('Validation.You can only input your native name in Chinese')
    }

    if (!values.firstName.trim()) {
      errors.firstName = t('Validation.Required')
    }

    if (!FullNameLettersPattern.test(values.lastName)) {
      errors.lastName = t('Validation.Last name contains unsupported characters')
    }
    if (values.middleName && !FullNameLettersPattern.test(values.middleName)) {
      errors.middleName = t('Validation.Middle name contains unsupported characters')
    }

    if (!values.lastName || values.lastName.length < 2) {
      errors.lastName = t('Validation.At least characters', {
        length: 2,
      })
    }

    if (values.lastName && values.lastName.length > 64) {
      errors.lastName = t('Validation.Last name must be 64 characters or fewer', {
        amount: 64,
      })
    }

    if (!values.lastName.trim()) {
      errors.lastName = t('Validation.Required')
    }

    if (!values.genderId) {
      errors.genderId = t('Validation.Required')
    }

    if (!values.monthOfBirth || isNaN(monthOfBirth) || monthOfBirth < 1 || monthOfBirth > 12) {
      errors.monthOfBirth = t('Validation.Enter a valid month')
    }

    if (
      !values.dayOfBirth ||
      isNaN(dayOfBirth) ||
      dayOfBirth < 1 ||
      dayOfBirth > 31 ||
      values.dayOfBirth.length > 2
    ) {
      errors.dayOfBirth = t('Validation.Enter a valid date')
    }

    if (
      !values.yearOfBirth ||
      isNaN(yearOfBirth) ||
      yearOfBirth <= 0 ||
      values.yearOfBirth.length !== 4 ||
      yearOfBirth <= 1900 ||
      yearOfBirth > eighteenYearsAgo
    ) {
      errors.yearOfBirth = t('Validation.Enter a valid year')
    }

    if (!errors.dayOfBirth && !errors.monthOfBirth && !errors.yearOfBirth) {
      if (monthOfBirth === 2 && !isLeapYear(yearOfBirth) && dayOfBirth > 28) {
        errors.dayOfBirth = t('Validation.Enter a valid date')
      }

      if (monthOfBirth === 2 && isLeapYear(yearOfBirth) && dayOfBirth > 29) {
        errors.dayOfBirth = t('Validation.Enter a valid date')
      }

      if ([4, 6, 9, 11].includes(monthOfBirth) && dayOfBirth > 30) {
        errors.dayOfBirth = t('Validation.Enter a valid date')
      }

      const dob = new Date(yearOfBirth, monthOfBirth - 1, dayOfBirth)

      if (isAfter(dob, subYears(new Date(), 18))) {
        errors.yearOfBirth = t('Validation.You must be 18 years of age or older')
      }
    }

    return errors
  },
  enableReinitialize: true,
})(PersonalDetailsStep1FormUI)
